import { createTheme, alpha } from '@mui/material/styles';

const palette = {
  primary: {
    main: '#4F46E5',
    light: '#818CF8',
    dark: '#3730A3',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#0EA5A4',
    light: '#5EEAD4',
    dark: '#115E59',
    contrastText: '#FFFFFF',
  },
  success: { main: '#10B981', light: '#A7F3D0', dark: '#047857' },
  warning: { main: '#F59E0B', light: '#FCD34D', dark: '#B45309' },
  error: { main: '#EF4444', light: '#FCA5A5', dark: '#B91C1C' },
  info: { main: '#3B82F6', light: '#93C5FD', dark: '#1D4ED8' },
  background: {
    default: '#F6F7FB',
    paper: '#FFFFFF',
    subtle: '#EEF0F6',
  },
  text: {
    primary: '#0F172A',
    secondary: '#475569',
    disabled: '#94A3B8',
  },
  divider: '#E5E7EB',
};

const theme = createTheme({
  palette,
  shape: {
    borderRadius: 12,
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    h1: { fontWeight: 700, letterSpacing: '-0.02em' },
    h2: { fontWeight: 700, letterSpacing: '-0.02em' },
    h3: { fontWeight: 700, letterSpacing: '-0.02em' },
    h4: { fontWeight: 700, letterSpacing: '-0.01em' },
    h5: { fontWeight: 600, letterSpacing: '-0.01em' },
    h6: { fontWeight: 600 },
    button: { fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
    body1: { lineHeight: 1.6 },
    body2: { lineHeight: 1.55 },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
  },
  shadows: [
    'none',
    '0 1px 2px rgba(15, 23, 42, 0.04)',
    '0 1px 3px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.04)',
    '0 4px 6px rgba(15, 23, 42, 0.05), 0 2px 4px rgba(15, 23, 42, 0.04)',
    '0 10px 15px rgba(15, 23, 42, 0.06), 0 4px 6px rgba(15, 23, 42, 0.04)',
    '0 20px 25px rgba(15, 23, 42, 0.08), 0 10px 10px rgba(15, 23, 42, 0.04)',
    ...Array(19).fill('0 25px 50px rgba(15, 23, 42, 0.12)'),
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': {
          '--safe-bottom': 'env(safe-area-inset-bottom, 0px)',
          '--safe-top': 'env(safe-area-inset-top, 0px)',
        },
        body: {
          backgroundColor: palette.background.default,
          color: palette.text.primary,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        '*': {
          boxSizing: 'border-box',
        },
        'input, textarea, select': {
          fontFamily: 'inherit',
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 12,
          paddingInline: 18,
          minHeight: 44,
          fontWeight: 600,
        },
        sizeLarge: { minHeight: 52, fontSize: '1rem' },
        sizeSmall: { minHeight: 36 },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: '0 8px 20px rgba(79, 70, 229, 0.25)' },
        },
        outlined: {
          borderColor: palette.divider,
          color: palette.text.primary,
          '&:hover': {
            borderColor: palette.primary.main,
            backgroundColor: alpha(palette.primary.main, 0.04),
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: '#FFFFFF',
          fontSize: 16,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.divider,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.primary.light,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.primary.main,
            borderWidth: 1.5,
          },
        },
        input: {
          padding: '14px 14px',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { fontSize: 15 },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: `1px solid ${palette.divider}`,
          backgroundColor: palette.background.paper,
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        rounded: { borderRadius: 16 },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 8, fontWeight: 500 },
        sizeSmall: { height: 24 },
      },
    },
    MuiAlert: {
      defaultProps: { variant: 'standard' },
      styleOverrides: {
        root: { borderRadius: 12, fontSize: 14 },
        standardInfo: {
          backgroundColor: alpha(palette.info.main, 0.08),
          color: palette.info.dark,
        },
        standardSuccess: {
          backgroundColor: alpha(palette.success.main, 0.1),
          color: palette.success.dark,
        },
        standardWarning: {
          backgroundColor: alpha(palette.warning.main, 0.1),
          color: palette.warning.dark,
        },
        standardError: {
          backgroundColor: alpha(palette.error.main, 0.1),
          color: palette.error.dark,
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          color: palette.background.subtle,
          '&.Mui-active': { color: palette.primary.main },
          '&.Mui-completed': { color: palette.success.main },
        },
        text: { fontWeight: 700, fill: palette.text.secondary },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          fontSize: 14,
          fontWeight: 500,
          '&.Mui-active': { fontWeight: 600, color: palette.primary.main },
          '&.Mui-completed': { color: palette.text.primary },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 6,
          borderRadius: 999,
          backgroundColor: palette.background.subtle,
        },
        bar: { borderRadius: 999 },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 20 },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: palette.text.primary,
          fontSize: 12,
          borderRadius: 8,
          padding: '6px 10px',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: { padding: 8 },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: { fontWeight: 600 },
      },
    },
  },
});

export default theme;
